import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ReactComponent as Logo } from '../logo.svg';
import { api } from '../api/client';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${api.baseURL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await response.json();

      if (!response.ok) {
        setError(data?.message || 'Invalid credentials. Please try again.');
        setLoading(false);
        return;
      }

      const userType = data?.user?.user_type ?? 'merchant';
      if (userType !== 'admin') {
        setError('Access denied. Admin account required.');
        setLoading(false);
        return;
      }

      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
      navigate('/admin/dashboard', { replace: true });
    } catch {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 text-gray-900 relative overflow-hidden">
      {/* Background pattern */}
      <div
        className="absolute inset-0 opacity-[0.4]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(148 163 184 / 0.15) 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-slate-200/30 blur-3xl -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-slate-300/20 blur-3xl translate-y-1/2 translate-x-1/2" />

      {/* Back to home - top left */}
      <motion.div
        className="relative z-10 p-6"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium text-sm transition-colors group"
        >
          <span className="w-8 h-8 rounded-lg border border-slate-200 bg-white/80 flex items-center justify-center text-slate-500 group-hover:border-slate-300 group-hover:bg-white shadow-sm transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </span>
          Back to home
        </Link>
      </motion.div>

      {/* Centered card */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-8">
        <motion.div
          className="w-full max-w-[420px]"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="rounded-2xl bg-white/90 backdrop-blur-xl shadow-xl shadow-slate-200/50 border border-slate-200/80 overflow-hidden">
            {/* Card header strip */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-8 py-6 text-center">
              <Link to="/" className="inline-flex items-center gap-2 mb-4">
                <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white">
                  <Logo className="w-6 h-6" />
                </span>
                <span className="font-bold text-lg text-white">SpynERP</span>
              </Link>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-2">
                <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-xs">⚙</span>
                <span className="text-sm font-semibold text-white/95">Admin sign in</span>
              </div>
            </div>

            {/* Form body */}
            <div className="px-8 py-8 sm:px-10 sm:py-10">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-extrabold text-slate-900 mb-1.5">
                  Admin access
                </h1>
                <p className="text-slate-600 text-sm">
                  Sign in with your admin account to manage SpynERP.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="rounded-xl bg-red-50 border border-red-200/80 px-4 py-3 text-sm text-red-700 text-center">
                    {error}
                  </div>
                )}
                <div>
                  <label htmlFor="admin-email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Email
                  </label>
                  <input
                    id="admin-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@spynerp.com"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-gray-900 placeholder-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20 focus:bg-white outline-none transition-all"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label htmlFor="admin-password" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Password
                  </label>
                  <input
                    id="admin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-gray-900 placeholder-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20 focus:bg-white outline-none transition-all"
                    autoComplete="current-password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full rounded-xl bg-slate-800 px-4 py-3.5 text-white font-semibold shadow-lg shadow-slate-800/25 hover:bg-slate-900 transition-all focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {loading ? 'Signing in...' : 'Sign in as admin'}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-500">
                Merchant account?{' '}
                <Link to="/login" className="font-semibold text-slate-600 hover:text-slate-800 transition-colors">
                  Sign in here →
                </Link>
              </p>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400">
            Secure admin access only. Do not share your credentials.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
